import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import logoImg from '../img/newlogo.png';

/**
 * Página de reautorización de Mercado Pago.
 *
 * Se abre desde el link que se le envía al dueño de una cuenta cuyo token venció.
 * No requiere estar logueado en la app: el contexto de qué cuenta se está
 * reautorizando viaja firmado en el parámetro `t` y se valida en el backend.
 */
function Reauth() {
    const location = useLocation();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [negocio, setNegocio] = useState(null);
    const [authUrl, setAuthUrl] = useState(null);

    useEffect(() => {
        const prepararReautorizacion = async () => {
            try {
                const params = new URLSearchParams(location.search);
                const t = params.get('t');

                if (!t) {
                    setError('El link no es válido. Pedí uno nuevo al equipo de TransferisOK.');
                    setLoading(false);
                    return;
                }

                const { data } = await axios.get(
                    `https://us-central1-transferisok.cloudfunctions.net/startReauth?t=${encodeURIComponent(t)}`
                );

                setNegocio(data.negocio);
                setAuthUrl(data.authUrl);
                setLoading(false);
            } catch (err) {
                console.error('Error preparando la reautorización:', err);
                setError(
                    err.response?.data?.error ||
                    'No pudimos validar el link. Puede haber caducado; pedí uno nuevo al equipo de TransferisOK.'
                );
                setLoading(false);
            }
        };

        prepararReautorizacion();
    }, [location]);

    return (
        <div className="login-container">
            <div className="login-card">
                <img src={logoImg} alt="TransferisOK" className="reauth-logo" />

                {loading && (
                    <div className="auth-loading-container">
                        <div className="auth-loader"></div>
                        <p>Verificando el link...</p>
                    </div>
                )}

                {!loading && error && (
                    <div className="error-message">
                        <p>{error}</p>
                    </div>
                )}

                {!loading && !error && (
                    <div className="reauth-content">
                        <h2>Reconectá tu cuenta de Mercado Pago</h2>
                        <p>
                            La conexión de {negocio ? <strong>{negocio}</strong> : 'tu cuenta'} con
                            Mercado Pago venció. Volvé a autorizarla para seguir viendo las
                            transferencias en tiempo real.
                        </p>
                        <p className="reauth-aviso">
                            Importante: autorizá con la <strong>misma cuenta de Mercado Pago</strong> que
                            venías usando. Si entrás con otra, la conexión va a ser rechazada.
                        </p>
                        <a href={authUrl} className="reauth-boton">
                            Reconectar con Mercado Pago
                        </a>
                        <p className="reauth-nota">
                            Tus operadores no necesitan hacer nada: se reconectan solos.
                        </p>
                    </div>
                )}
            </div>

            <style jsx>{`
                .reauth-logo {
                    display: block;
                    max-width: 180px;
                    margin: 0 auto 24px;
                }

                .auth-loading-container {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    padding: 30px;
                    text-align: center;
                }

                .auth-loader {
                    border: 5px solid #f3f3f3;
                    border-top: 5px solid #001c4d;
                    border-radius: 50%;
                    width: 50px;
                    height: 50px;
                    animation: spin 1.5s linear infinite;
                    margin-bottom: 20px;
                }

                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }

                .reauth-content {
                    text-align: center;
                    padding: 10px 20px 20px;
                }

                .reauth-content h2 {
                    color: #001c4d;
                    margin-bottom: 16px;
                }

                .reauth-content p {
                    color: #64748b;
                    font-size: 16px;
                    line-height: 1.5;
                    margin-bottom: 16px;
                }

                .reauth-aviso {
                    background: #fff8e1;
                    border-left: 4px solid #f59e0b;
                    padding: 12px 16px;
                    text-align: left;
                    border-radius: 4px;
                    font-size: 15px !important;
                }

                .reauth-boton {
                    display: inline-block;
                    background: #009ee3;
                    color: #fff;
                    text-decoration: none;
                    padding: 14px 32px;
                    border-radius: 8px;
                    font-weight: 600;
                    font-size: 16px;
                    margin: 8px 0 16px;
                    transition: background 0.2s;
                }

                .reauth-boton:hover {
                    background: #0081ba;
                }

                .reauth-nota {
                    font-size: 14px !important;
                    color: #94a3b8 !important;
                    margin-bottom: 0 !important;
                }
            `}</style>
        </div>
    );
}

export default Reauth;
